import { GetXcoreStaticLayout, useData } from "@robe/client/xcore-ui";
import {
  Content,
  fetchRelationsForMixedContents,
  GetInstagramPostsResponse,
  parseQueryParamsObject,
  resolveLocalizedValue,
  sentryLogger,
  XcoreCore,
} from "@robe/core";
import { SubHeaderContentsRes } from "pages/api/robe/subheader";
import { smartLinksFields } from "./relationsFields";
import {
  AboutUs,
  AboutUsValues,
  AccountUpdate,
  ApplicationPicker,
  Cookies,
  CookiesPage,
  CookiesValues,
  Disclaimer,
  Exhibitions,
  General,
  Homepage,
  Innovations,
  NewsPage,
  Offices,
  OfficesValues,
  Patents,
  Privacy,
  Products,
  ProductsValues,
  SearchPage,
  SpotOn,
  SpotOnValues,
  Support,
  Sustainability,
  Trademarks,
  UnavailableLocaleError,
  UnavailableLocaleErrorValues,
  UserLoginPage,
  UserLoginPageValues,
} from "./types";

type LayoutContentsCommonFields = "title";

type LayoutHomepage = Homepage<LayoutContentsCommonFields>;
type LayoutSupport = Support<LayoutContentsCommonFields>;
type LayoutNewsPage = NewsPage<LayoutContentsCommonFields>;
type LayoutExhibitions = Exhibitions<LayoutContentsCommonFields>;
type LayoutInnovations = Innovations<LayoutContentsCommonFields>;
type LayoutDisclaimer = Disclaimer<LayoutContentsCommonFields>;
type LayoutPrivacy = Privacy<LayoutContentsCommonFields>;
type LayoutPatents = Patents<LayoutContentsCommonFields>;
type LayoutTrademarks = Trademarks<LayoutContentsCommonFields>;
type LayoutSustainability = Sustainability<LayoutContentsCommonFields>;
type LayoutSearchPage = SearchPage<LayoutContentsCommonFields>;
type LayoutCookiesPage = CookiesPage<LayoutContentsCommonFields>;
type LayoutAccountUpdate = AccountUpdate<LayoutContentsCommonFields>;

// LayoutOffices
type LayoutOfficesValuesFields =
  | LayoutContentsCommonFields
  | "officeList"
  | "defaultContactPage"
  | "personShowMore"
  | "mobileDropdownPlaceholder";
type LayoutOfficesValues = Pick<OfficesValues, LayoutOfficesValuesFields>;
type LayoutOffices = Offices<LayoutOfficesValuesFields>;

// LayoutAboutUs
type LayoutAboutUsValuesFields =
  | LayoutContentsCommonFields
  | "owners"
  | "history"
  | "legal"
  | "career"
  | "dropdownCareerCz"
  | "careerExtLink"
  | "madeInEUHeading";
type LayoutAboutUsValues = Pick<AboutUsValues, LayoutAboutUsValuesFields>;
type LayoutAboutUs = AboutUs<LayoutAboutUsValuesFields>;

// LayoutSpotOn
type LayoutSpotOnValuesFields = LayoutContentsCommonFields | "itemsRelation";
type LayoutSpotOnValues = Pick<SpotOnValues, LayoutSpotOnValuesFields>;
type LayoutSpotOn = SpotOn<LayoutSpotOnValuesFields>;

// LayoutProducts
type LayoutProductsValuesFields = LayoutContentsCommonFields | "itemsRelation" | "newIconText" | "middlePageContent";
type LayoutProductsValues = Pick<ProductsValues, LayoutProductsValuesFields>;
export type LayoutProducts = Products<LayoutProductsValuesFields>;

// LayoutCookies
type LayoutCookiesValuesFields = "btnMore" | "message" | "btnAgreed" | "btnDecline" | "btnMoreRel";
type LayoutCookiesValues = Pick<CookiesValues, LayoutCookiesValuesFields>;
type LayoutCookies = Cookies<LayoutCookiesValuesFields>;

// LayoutUserLoginPage
type LayoutUserLoginPageValuesFields = "userLoginHeading";
type LayoutUserLoginPageValues = Pick<UserLoginPageValues, LayoutUserLoginPageValuesFields>;
type LayoutUserLoginPage = UserLoginPage<LayoutUserLoginPageValuesFields>;

// UnavailableLocaleError
type LayoutUnavailableLocaleErrorValuesFields = LayoutContentsCommonFields | "message";
type LayoutUnavailableLocaleErrorValues = Pick<UnavailableLocaleErrorValues, LayoutUnavailableLocaleErrorValuesFields>;
type LayoutUnavailableLocaleError = UnavailableLocaleError<LayoutUnavailableLocaleErrorValuesFields>;

export interface LayoutContent {
  general: General;
  subheader: {
    applicationPicker: ApplicationPicker;
    items: SubHeaderContentsRes;
  };
  header: {
    nav: {
      homepage: LayoutHomepage;
      spotOn: LayoutSpotOn;
      products: LayoutProducts;
      support: LayoutSupport;
      newsPage: LayoutNewsPage;
      exhibitions: LayoutExhibitions;
      aboutUs: LayoutAboutUs;
      innovations: LayoutInnovations;
      offices: LayoutOffices;
      disclaimer: LayoutDisclaimer;
      privacy: LayoutPrivacy;
      patents: LayoutPatents;
      trademarks: LayoutTrademarks;
      sustainability: LayoutSustainability;
    };
    searchPage: LayoutSearchPage;
  };
  cookies: LayoutCookies;
  cookiesPage: LayoutCookiesPage;

  userLoginPage: LayoutUserLoginPage;
  accountUpdate: LayoutAccountUpdate;

  unavailableLocalePage: LayoutUnavailableLocaleError;

  instagramPosts: GetInstagramPostsResponse;
}

export const useLayout = <T extends LayoutContent>() => useData<Content, [], [], any, T>();

type LayoutContentTypes =
  | General
  | ApplicationPicker
  | LayoutHomepage
  | LayoutSpotOn
  | LayoutProducts
  | LayoutSupport
  | LayoutNewsPage
  | LayoutExhibitions
  | LayoutAboutUs
  | LayoutInnovations
  | LayoutOffices
  | LayoutDisclaimer
  | LayoutPrivacy
  | LayoutPatents
  | LayoutTrademarks
  | LayoutSustainability
  | LayoutSearchPage
  | LayoutCookies
  | LayoutCookiesPage
  | LayoutUserLoginPage
  | LayoutAccountUpdate
  | LayoutUnavailableLocaleError;

type LayoutContentValues = LayoutOfficesValues &
  LayoutAboutUsValues &
  LayoutSpotOnValues &
  LayoutProductsValues &
  LayoutCookiesValues &
  LayoutUserLoginPageValues &
  LayoutUnavailableLocaleErrorValues;

type CommonLayoutFields =
  | keyof LayoutOfficesValues
  | keyof LayoutAboutUsValues
  | keyof LayoutSpotOnValues
  | keyof LayoutProductsValues
  | keyof LayoutCookiesValues
  | keyof LayoutUserLoginPageValues
  | keyof LayoutUnavailableLocaleErrorValues;

interface FetchSubheaderProps {
  core: XcoreCore;
  products: LayoutProducts;
}

const fetchSubheader = async ({ core, products }: FetchSubheaderProps) => {
  const items = products?.values?.middlePageContent?.values?.itemsRelation
    ? (resolveLocalizedValue(core.currentLocale, core.locales)(products?.values?.middlePageContent?.values?.itemsRelation)?.value ?? [])
    : [];
  const subHeaderItemsIds = items.map((item) => item.id);

  if (subHeaderItemsIds.length === 0) {
    return [];
  }

  const host = core.mainLocaleDomain
    ? `https://${core.mainLocaleDomain.domain}`
    : `http://localhost:${process.env.NEXT_PUBLIC_PORT ?? 3000}`;
  const params = parseQueryParamsObject({ locale: core.currentLocale, ids: subHeaderItemsIds });

  const url = `${host}/api/robe/subheader${params}`;

  try {
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();

      const sortedItems =
        items && data // preserve order from CMS
          ? items.map(({ id }) => (data as SubHeaderContentsRes)?.find((d) => d.id === id)).filter(Boolean)
          : [];

      return sortedItems;
    }

    return [];
  } catch (error) {
    sentryLogger({
      error,
      message: "Unknown error occurred during subheader layout fetch",
      context: {
        requestUrl: url,
      },
    });

    return [];
  }
};

export const getLayout: GetXcoreStaticLayout = async ({ core }) => {
  try {
    const fields: CommonLayoutFields[] = [
      "title",
      "owners",
      "history",
      "legal",
      "career",
      "dropdownCareerCz",
      "careerExtLink",
      "madeInEUHeading",
      "btnMoreRel",
      "message",
      "btnAgreed",
      "btnMore",
      "btnDecline",
      "middlePageContent",
      "defaultContactPage",
      "userLoginHeading",
      "itemsRelation",
      "newIconText",
      "personShowMore",
      "officeList",
      "mobileDropdownPlaceholder",
    ];

    const [contents, contentsWithSpecifiedFields, instagramPosts] = await Promise.all([
      // Without fields specified
      core.content
        .listByTypes<LayoutContentTypes>(["general", "applicationPicker"], {
          includeValues: true,
          includeNonLocalized: true,
        })
        .then(
          fetchRelationsForMixedContents({
            core,
            relationSourceFields: [
              // Application picker
              {
                field: "relation",
                ofListObjectField: "applications",
              },
              // General
              { field: "followUsLinks" },
              { field: "helpdeskLinks" },
              { field: "navigateLinks" },
              { field: "accountUpdate" },
              { field: "inTouchLinks" },
            ],
            options: {
              fields: smartLinksFields,
            },
            subRelations: {
              relationSourceFields: [{ field: "relation" }],
            },
          })
        ),
      // With fields specified
      core.content
        .listByTypes<LayoutContentTypes, LayoutContentValues>(
          [
            "aboutUs",
            "homepage",
            "support",
            "newsPage",
            "exhibitions",
            "disclaimer",
            "privacy",
            "patents",
            "trademarks",
            "cookiesPage",
            "searchPage",
            "userLoginPage",
            "accountUpdate",
            "cookies",
            "innovations",
            "sustainability",
            "offices",
            "spotOn",
            "products",
            "unavailableLocaleError",
          ],
          {
            includeNonLocalized: true,
            fields,
          }
        )
        .then(
          fetchRelationsForMixedContents({
            core,
            relationSourceFields: [
              // Subheader
              { field: "middlePageContent" },
              // Office
              {
                field: "offices",
                ofListObjectField: "officeList",
              },
              { field: "defaultContactPage" },
              // SpotOn & Products
              { field: "itemsRelation" },
              // Cookies
              { field: "btnMoreRel" },
            ],
            options: {
              fields: [
                // for "middlePageContent"
                "itemsRelation",
              ],
            },
            subRelations: {
              // Products > Product overview page
              relationSourceFields: [{ field: "itemsRelation" }],
            },
          })
        ),
      core.web.getInstagramPosts(),
    ]);

    const fetchedContents = [...contents.contents, ...contentsWithSpecifiedFields.contents];
    const fetchedContentsDistributed: Record<string, LayoutContentTypes> = {};

    fetchedContents.forEach((content) => {
      fetchedContentsDistributed[content.type] = content as any;
    });

    const { general, applicationPicker, searchPage, cookies, cookiesPage, userLoginPage, accountUpdate, unavailableLocaleError, ...nav } =
      fetchedContentsDistributed;

    const subheaderItems = await fetchSubheader({ core, products: nav.products as LayoutProducts });

    return {
      general,
      subheader: {
        applicationPicker,
        items: subheaderItems,
      },
      header: {
        nav,
        searchPage,
      },
      cookies,
      cookiesPage,
      userLoginPage,
      accountUpdate,
      unavailableLocalePage: unavailableLocaleError,
      instagramPosts,
    };
  } catch (error) {
    sentryLogger({
      error,
      message: "Unknown error occurred during layout contents fetch",
    });

    throw error;
  }
};
